<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t, locale } = useI18n()
import { useLogger } from 'vue-logger-plugin'
import { ref, onMounted, computed } from 'vue'
import { setStoreLogger } from '../../logger.ts'
import { Grid, type GridCellProps } from '@progress/kendo-vue-grid'
import { Drawer, DrawerItem } from '@progress/kendo-vue-layout'
import { DropDownList } from '@progress/kendo-vue-dropdowns';
import { TextBox as KTextBox, NumericTextBox as KNumericTextBox } from '@progress/kendo-vue-inputs'
import { DatePicker,  type DatePickerChangeEvent } from '@progress/kendo-vue-dateinputs'
import { LocalizationProvider } from '@progress/kendo-vue-intl'
import { useDatePickerLocalization } from '../../composables/useDatePickerLocalization'
import type { GridSortChangeEvent } from '@progress/kendo-vue-grid'
import { GetInvoiceList, CancelInvoiceCart, ResendInvoiceCart } from '../../services/CartService'
import { handleError } from '../../services/ErrorService.ts'
import { GetCodesFromLocalStorageOrApi } from '../../services/CountryCultureService'
import { FormatCurrency } from '../../services/currencyFormatter.ts'
import type { IAoeCart } from '../../types/CartTypes.d.ts'
import type { SortDescriptor } from '@progress/kendo-data-query'
import { GetPartyOrderEntryUrl, GetInvoicePaymentUrl, GetContactOrderEntryUrl } from '../../stores/SettingsStore.ts'
import { isInvoiceClosed } from '../../constants/InvoiceStatus.ts'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { Menu as kMenu, type MenuSelectEvent, type MenuItemModel } from '@progress/kendo-vue-layout'
import { formatDateWithShortMonthAndTime, formatDateAsDDMMYYYY } from '../../api/Common.ts';
import { filterIcon, moreVerticalIcon } from '@progress/kendo-svg-icons'
import Swal from 'sweetalert2'
import { resolveColumnWidth } from '../../utils/kendoHelper.ts'


const logger = useLogger()
setStoreLogger(logger)
logger.debug('AoeDashboardInvoiceList.vue')

const { dateFormat, formatPlaceholder } = useDatePickerLocalization(locale)

interface DDLOption {
  text: string
  id: string
  type?: string
}

const loader = ref<boolean>(false)
const sort = ref<SortDescriptor[]>([{ field: 'invoice.issueDate', dir: 'desc' }])
const gridData = ref<IAoeCart[]>([])
const origGridData = ref<IAoeCart[]>([])
const partyOrderEntryURL = ref<string>('')
const contactOrderEntryURL = ref<string>('')
const invoicePaymentURL = ref<string>('')
const filterExpanded = ref<boolean>(false)
const selectedInvoiceStatus = ref<DDLOption>({ text: '', id: '' })
const invoiceStatusDDL = ref<DDLOption[]>([])
const periodDDL = ref<DDLOption[]>([])
const selectedPeriod = ref<DDLOption>({ text: '', id: '' })
const columnDDL = ref<DDLOption[]>([])
const selectedColumn = ref<DDLOption>({ text: '', id: '', type: '' })
const columnOperandDDL = ref<DDLOption[]>([])
const selectedColumnOperand = ref<DDLOption>({ text: '', id: '' })
const searchText = ref<string>('')
const searchNumber = ref<number | undefined>(undefined)
const searchSecondNumber = ref<number | undefined>(undefined)
const txtDate = ref<Date | undefined>(undefined)
const txtSecondDate = ref<Date | undefined>(undefined)

const filterDrawerItems = ref([
  {
    text: t('orderList.FilterList'),
    selected: false,
    data: { path: '/' },
  },
])

const columns = [
  {
    field: 'aoeCartId',
    title: t('dashboard.invoice.orderNumber'),
    width: resolveColumnWidth('100px', t('dashboard.invoice.orderNumber')),
    cell: 'orderNumberTemplate'
  },
  {
    field: 'eventId',
    title: t('dashboard.invoice.eventId'),
    width: resolveColumnWidth('100px', t('dashboard.invoice.eventId'))
  },
  {
    field: 'customerName',
    title: t('dashboard.invoice.customerName'),
    width: resolveColumnWidth('150px', t('dashboard.invoice.customerName')),
    cell: 'customerNameTemplate'
  },
  {
    field: 'invoice.invoiceNumber',
    title: t('dashboard.invoice.invoiceNumber'),
    width: resolveColumnWidth('160px', t('dashboard.invoice.invoiceNumber')),
    cell: 'invoiceNumberTemplate'
  },
  {
    field: 'invoice.numberOfOpens',
    title: t('dashboard.invoice.reads'),
    width: resolveColumnWidth('80px', t('dashboard.invoice.reads'))
  },
  {
    field: 'total',
    title: t('dashboard.invoice.amount'),
    width: resolveColumnWidth('100px', t('dashboard.invoice.amount')),
    cell: 'amountTemplate'
  },
  {
    field: 'invoice.issueDate',
    title: t('dashboard.invoice.issueDate'),
    width: resolveColumnWidth('140px', t('dashboard.invoice.issueDate')),
    cell: 'invoiceIssueDateTemplate'
  },
  {
    field: 'invoice.invoiceStatus.localizedValue',
    title: t('dashboard.invoice.status'),
    width: resolveColumnWidth('120px', t('dashboard.invoice.status')),
    cell: 'statusTemplate'
  },
  {
    field: '',
    title: '',
    width: 50,
    sortable: false,
    cell: actionCellFunction
  }
]

const activeTab = ref('NotPaid')

// Mobile pagination
const currentPage = ref(1)
const itemsPerPage = ref(5)
const paginatedData = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return gridData.value.slice(start, end)
})
const totalPages = computed(() => Math.ceil(gridData.value.length / itemsPerPage.value))

function goToPage(page: number) {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
    // Scroll to top of mobile card view
    const mobileView = document.querySelector('.mobile-card-view')
    if (mobileView) {
      mobileView.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }
}

function nextPage() {
  goToPage(currentPage.value + 1)
}

function previousPage() {
  goToPage(currentPage.value - 1)
}

// A canceled or paid invoice is terminal: the invoice payment page only accepts ISSUED, so
// resending one would email the customer a link that cannot be paid.
const TERMINAL_INVOICE_STATUSES = ['canceled', 'paid']

function isInvoiceActionable(invoice: IAoeCart) {
  const codeId = invoice?.invoice?.invoiceStatus?.codeId?.toLowerCase()
  return codeId !== undefined && !TERMINAL_INVOICE_STATUSES.includes(codeId)
}

function actionCellFunction(
  h: typeof import('vue').h,
  tdElement: string | object,
  props: GridCellProps,
) {
  return h(tdElement, [
    h(
      kMenu,
      {
        items: GetMenuItems(props.dataItem),
        onSelect: (e: MenuSelectEvent) => selectHandler(e, props.dataItem),
        class: 'invoice-grid-action-menu'
      },
      ''
    ),
  ])
}

function GetMenuItems(invoice: IAoeCart) {
  const actionable = isInvoiceActionable(invoice)

  const items: MenuItemModel[] = [
    {
      // Kendo exposes `data` for custom payload; keying off it survives translation changes.
      text: t('dashboard.invoice.CancelInvoice'),
      data: 'CancelInvoice',
      disabled: !actionable,
    },
    {
      text: t('dashboard.invoice.ResendInvoice'),
      data: 'ResendInvoice',
      disabled: !actionable,
    },
  ]

  // Say why the actions are greyed out instead of leaving the consultant guessing. Disabled so
  // it stays inert, and derived from the same status the actions are disabled on.
  if (!actionable) {
    const codeId = invoice?.invoice?.invoiceStatus?.codeId?.toLowerCase()
    items.push({ separator: true })
    items.push({
      text: codeId === 'canceled'
        ? t('dashboard.invoice.ActionsUnavailableCanceled')
        : t('dashboard.invoice.ActionsUnavailablePaid'),
      disabled: true,
      cssClass: 'invoice-action-reason',
    })
  }

  const menuItems: MenuItemModel[] = [
    {
      svgIcon: moreVerticalIcon,
      // Named so the CSS below can reach the popup, which Kendo teleports out of this component.
      popupClass: 'invoice-action-popup',
      items,
    },
  ]

  return menuItems
}

function selectHandler(event: MenuSelectEvent, invoice: IAoeCart) {
  logger.debug('selectHandler invoice:', invoice)

  const action = event.item.data
  if (action !== 'CancelInvoice' && action !== 'ResendInvoice') return

  // The menu item is already disabled for terminal statuses; this guards a stale grid row.
  if (!isInvoiceActionable(invoice)) {
    Swal.fire({
      title: t('alerts.InvoiceChange'),
      text: action === 'CancelInvoice' ? t('alerts.CannotCancelInvoice') : t('alerts.CannotResendInvoice'),
      icon: 'error',
    })
    return
  }

  const isCancel = action === 'CancelInvoice'

  Swal.fire({
    title: t('alerts.InvoiceChange'),
    text: isCancel ? t('alerts.CancelInvoiceConfirm') : t('alerts.ResendInvoiceConfirm'),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: t('buttons.Yes'),
    cancelButtonText: t('buttons.No'),
  }).then(result => {
    if (!result.isConfirmed) return

    const request = { aoeCartIdToken: invoice.aoeCartIdToken }
    const action$ = isCancel ? CancelInvoiceCart(request) : ResendInvoiceCart(request)

    action$
      .then(() => {
        Swal.fire(
          t('alerts.Success'),
          isCancel ? t('alerts.InvoiceCancelled') : t('alerts.InvoiceResent'),
          'success',
        )
        // Cancel changes the stored status, resend does not — only the former needs a reload.
        if (isCancel) getInvoiceList()
      })
      .catch(async error => {
        await handleError(error, t)
      })
  })
}

function sortChangeHandler(e: GridSortChangeEvent) {
  loader.value = true
  if (e.sort[0] !== undefined) {
    sort.value = e.sort
  } else {
    logger.debug('sortChangeHandler: e.sort[0] is undefined')
  }
  loader.value = false
}

function applyActiveTabFilter() {
  if (activeTab.value === 'all') {
    gridData.value = origGridData.value
  } else if (activeTab.value === 'NotPaid') {
    gridData.value = origGridData.value.filter((invoice) => invoice.invoice?.invoiceStatus.codeId.toLowerCase() !== 'paid' && invoice.invoice?.invoiceStatus.codeId.toLowerCase() !== 'canceled')
  } else if (activeTab.value === 'Paid') {
    gridData.value = origGridData.value.filter((invoice) => invoice.invoice?.invoiceStatus.codeId.toLowerCase() === 'paid')
  }
}

function setActiveTab(tab: string) {
  activeTab.value = tab
  currentPage.value = 1 // Reset to first page when changing tabs
  applyActiveTabFilter()
}

// The order-number link "reopens" the order for editing. Event orders always
// route to party order entry via their event token. Non-event (individual /
// contact) orders reopen the customer's active cart via contact order entry -
// but that only exists while the invoice is still open; once it is paid or
// canceled the cart is closed, so those rows are not linked at all (see
// isOrderViewable). The invoice-number link still provides their receipt.
function isOrderViewable(invoice: IAoeCart): boolean {
  if (invoice.eventIdToken) {
    return true
  }
  return invoice.beeNumberCustomer != null && !isInvoiceClosed(invoice.invoice?.invoiceStatus.codeId)
}

function viewOrder(
  event: MouseEvent,
  customerBeeNumber: number | null,
  eventIdToken: string | undefined,

) {
  logger.debug('viewOrder', event, customerBeeNumber)
  if (eventIdToken) {
    let url = partyOrderEntryURL.value + '?event=' + eventIdToken
    if (customerBeeNumber != null) {
      url = url + '&customer=' + customerBeeNumber
    }
    window.location.href = url
  } else if (customerBeeNumber != null) {
    window.location.href = contactOrderEntryURL.value + '?contactBeeNumber=' + customerBeeNumber
  }
}

function viewInvoice(
  event: MouseEvent,
  invoiceNumber: string,

) {
  logger.debug('viewInvoice', event, invoiceNumber)
  const url = invoicePaymentURL.value + '?invoicenumber=' + invoiceNumber + '&isadmin=true'
  window.location.href = url
}

onMounted(async () => {
  logger.debug('onMounted')
  invoicePaymentURL.value = await GetInvoicePaymentUrl()
  partyOrderEntryURL.value = await GetPartyOrderEntryUrl()
  contactOrderEntryURL.value = await GetContactOrderEntryUrl()

  const statusAll = { text: t('dashboard.invoice.AllStatuses'), id: 'All' }
  invoiceStatusDDL.value.push(statusAll)
  selectedInvoiceStatus.value = statusAll

  GetCodesFromLocalStorageOrApi('PAYMENTINVOICESTATUS').then(data => {
    if (data)
    {
      for (const codes of data.items) {
          invoiceStatusDDL.value.push({ text: codes.codeText, id: codes.codeId })
      }
    }
  }).catch(async error => {
    await handleError(error, t)
  })

  const periodAll = { text: t('dashboard.invoice.AllPeriods'), id: 'All' }
  periodDDL.value.push(periodAll)
  selectedPeriod.value = periodAll

  periodDDL.value.push({ text: t('dashboard.invoice.Last30Days'), id: '30' })
  periodDDL.value.push({ text: t('dashboard.invoice.Last60Days'), id: '60' })
  periodDDL.value.push({ text: t('dashboard.invoice.Last90Days'), id: '90' })

  const columnAll = { text: t('orderList.NoColumnSelected'), id: '', type: '' }
  columnDDL.value.push(columnAll)
  selectedColumn.value = columnAll

  for (const column of columns) {
    if (column.field === '' || column.field === 'total' || column.field === 'invoice.numberOfOpens' || column.field === 'invoice.invoiceStatus.localizedValue') {
      continue; // Skip the column as it is handled separately
    }

    let columnType = 'string';
    if (column.field == "invoice.issueDate")
    {
      columnType = 'date';
    }
    else if (column.field == "aoeCartId" || column.field == "eventId")
    {
      columnType = 'number';
    }

    columnDDL.value.push({ text: column.title, id: column.field, type: columnType })
  }

  SetColumnOperandDDLValues()

  getInvoiceList()

})

function ReLoadGrid()
{
  loader.value = true;
  gridData.value = []
  origGridData.value = []
  sort.value = [{ field: 'invoice.issueDate', dir: 'desc' }]
  HideNoRecordsWhileLoading()
}

function getInvoiceList() {
  ReLoadGrid()

  GetInvoiceList().then((invoiceResp) => {
    if (invoiceResp)
    {
      for (const invoice of invoiceResp.items) {
        invoice.customerName = invoice.customerFirstName + ' ' + invoice.customerLastName
        origGridData.value.push(invoice)
      }

      applyActiveTabFilter()

      /* TEAU-1239: Mock data commented out - was for testing only
      const mockData = [
        { aoeCartId: 558, eventId: 20031, customerName: 'Evan Christianson', invoice: { invoiceNumber: '558F05C5409454', issueDate: '2026-03-20', invoiceStatus: { codeId: 'ISSUED', localizedValue: 'Issued', categoryId: 'STATUS' }, numberOfOpens: 0 }, total: 56.90, currencyId: 'EUR', customerFirstName: 'Evan', customerLastName: 'Christianson', eventTitle: 'AOE TEST 13 TEST' },
        { aoeCartId: 627, eventId: 20038, customerName: 'first last', invoice: { invoiceNumber: '627CFF92365B3A', issueDate: '2025-11-12', invoiceStatus: { codeId: 'ISSUED', localizedValue: 'Issued', categoryId: 'STATUS' }, numberOfOpens: 0 }, total: 156.90, currencyId: 'EUR', customerFirstName: 'first', customerLastName: 'last', eventTitle: 'test' },
        { aoeCartId: 688, eventId: 20031, customerName: 'Amanda Molley', invoice: { invoiceNumber: '6885723D9E23AB', issueDate: '2026-03-20', invoiceStatus: { codeId: 'ISSUED', localizedValue: 'Issued', categoryId: 'STATUS' }, numberOfOpens: 0 }, total: 1156.90, currencyId: 'EUR', customerFirstName: 'Amanda', customerLastName: 'Molley', eventTitle: 'AOE TEST 13 TEST' },
      ] as any
      for (const mock of mockData) {
        gridData.value.push(mock)
        origGridData.value.push(mock)
      }
      */
      // Keep the tab the consultant is looking at applied across reloads.
      setActiveTab(activeTab.value)
      loader.value = false;
      HideNoRecordsWhileLoading()
    }
  }).catch(async error => {
    loader.value = false;
    HideNoRecordsWhileLoading()
    await handleError(error, t)
  })
}

function HideNoRecordsWhileLoading() {
  const invoiceListDiv = document.getElementById('invoiceListGridRowDiv') as HTMLElement
  const classAttribute = invoiceListDiv.attributes.getNamedItem('class')
  if (classAttribute) {
    if (loader.value == false) {
      classAttribute.value = 'invoices-grid desktop-grid-view'
    } else {
      classAttribute.value = 'invoices-grid grid-loading'
    }
  }
}

function gridFilterDrawerClick() {
  logger.debug('gridFilterDrawerClick')

  // Set the drawer top position based on current scroll position
  if (!filterExpanded.value && window.matchMedia('(max-width: 768px)').matches) {
    const viewportTop = window.visualViewport?.pageTop
    console.log(viewportTop)
    const scrollY = viewportTop ?? window.scrollY ?? window.pageYOffset ?? 0
    console.log(scrollY)
    document.documentElement.style.setProperty('--drawer-top', `${Math.max(0, Math.round(scrollY))}px`)
  }

  filterExpanded.value = !filterExpanded.value
}

function dropDownChangeHandler(id: string, option: DDLOption) {
  logger.debug('dropDownChangeHandler')
  if (id === 'invoiceStatusDDL') {
    selectedInvoiceStatus.value = option
  } else if (id === 'periodDDL') {
    selectedPeriod.value = option
  } else if (id === 'columnDDL') {
    selectedColumn.value = option
    SetColumnOperandDDLValues()
  } else if (id === 'columnOperandDDL') {
    selectedColumnOperand.value = option
  }
}

function SetColumnOperandDDLValues()
{
  columnOperandDDL.value = []

  if (selectedColumn.value.id === '') {
    const columnOperandNeedColumn = { text: t('orderList.ColumnOperandSelectColumn'), id: '' }
    columnOperandDDL.value.push(columnOperandNeedColumn)
    selectedColumnOperand.value = columnOperandNeedColumn
    return
  }

  const columnOperandEquals = { text: t('orderList.ColumnOperandEquals'), id: 'Equals' }
  columnOperandDDL.value.push(columnOperandEquals)
  selectedColumnOperand.value = columnOperandEquals

  columnOperandDDL.value.push({ text: t('orderList.ColumnOperandNotEquals'), id: 'NotEquals' })

  if (selectedColumn.value.type === 'date' || selectedColumn.value.type === 'number') {
    columnOperandDDL.value.push({ text: t('orderList.ColumnOperandGreaterThan'), id: 'GreaterThan' })
    columnOperandDDL.value.push({ text: t('orderList.ColumnOperandGreaterThanOrEqual'), id: 'GreaterThanOrEqual' })
    columnOperandDDL.value.push({ text: t('orderList.ColumnOperandLessThan'), id: 'LessThan' })
    columnOperandDDL.value.push({ text: t('orderList.ColumnOperandLessThanOrEqual'), id: 'LessThanOrEqual' })
  }

  if (selectedColumn.value.type === 'string') {
    columnOperandDDL.value.push({ text: t('orderList.ColumnOperandBeginsWith'), id: 'BeginsWith' })
    columnOperandDDL.value.push({ text: t('orderList.ColumnOperandContains'), id: 'Contains' })
  }

  if (selectedColumn.value.type === 'date' || selectedColumn.value.type === 'number') {
    columnOperandDDL.value.push({ text: t('orderList.ColumnOperandBetween'), id: 'Between' })
  }
}

function dateChangeHandler(event: DatePickerChangeEvent) {
  logger.debug('dateChangeHandler event:', event)
  if (event.value != null) {
    txtDate.value = event.value
  }
}

function secondDateChangeHandler(event: DatePickerChangeEvent) {
  logger.debug('secondDateChangeHandler event:', event)
  if (event.value != null) {
    txtSecondDate.value = event.value
  }
}

function getValueByPath<T>(obj: T, path: string): unknown {
  return path.split('.').reduce((o: unknown, key) => (o && typeof o === 'object' ? (o as Record<string, unknown>)[key] : undefined), obj);
}

function applyGridFilter() {
    logger.debug('applyGridFilter')

    filterExpanded.value = false
    currentPage.value = 1 // Reset to first page when applying filter

    gridData.value = origGridData.value

    if (selectedInvoiceStatus.value.id !== 'All') {
        gridData.value = gridData.value.filter(item => item.invoice?.invoiceStatus.codeId === selectedInvoiceStatus.value.id);
    }

    if (selectedPeriod.value.id !== 'All') {
      const today = new Date();
      const pastDate = new Date();
      pastDate.setDate(today.getDate() - Number(selectedPeriod.value.id));
      gridData.value = gridData.value.filter(item => item.invoice && new Date(item.invoice?.issueDate) >= pastDate);
    }

    if (selectedColumn.value.id !== '' && (searchText.value.length > 0 || txtDate.value !== undefined || searchNumber.value !== null)) {
        const columnField = selectedColumn.value.id;
        const columnOperand = selectedColumnOperand.value.id;

        const search = searchText.value.length > 0 ? searchText.value : String(searchNumber.value);

        if (columnOperand === 'Equals') {
            gridData.value = gridData.value.filter(item =>
              {
                const value = getValueByPath(item, columnField)

                return selectedColumn.value.id === "invoice.issueDate" ?
                new Date(value as string).getFullYear() === txtDate.value?.getFullYear() &&
                new Date(value as string).getMonth() === txtDate.value?.getMonth() &&
                new Date(value as string).getDate() === txtDate.value?.getDate()
                :  String(value).toLowerCase() === search.toLowerCase();
              }
            );
        }
        else if (columnOperand === 'NotEquals') {
            gridData.value = gridData.value.filter(item =>
              {
                const value = getValueByPath(item, columnField);
                return selectedColumn.value.id === "invoice.issueDate" ?
                new Date(value as string).getFullYear() !== txtDate.value?.getFullYear() ||
                new Date(value as string).getMonth() !== txtDate.value?.getMonth() ||
                new Date(value as string).getDate() !== txtDate.value?.getDate()
                : String(value).toLowerCase() !== search.toLowerCase();
              }
            );
        }
        else if (columnOperand === 'GreaterThan') {
            gridData.value = gridData.value.filter(item =>
              {
                const value = getValueByPath(item, columnField);
                return selectedColumn.value.id === "invoice.issueDate" && txtDate.value ?
                new Date(value as string).getFullYear() > txtDate.value?.getFullYear() ||
                (new Date(value as string).getFullYear() >= txtDate.value?.getFullYear() && new Date(value as string).getMonth() > txtDate.value?.getMonth()) ||
                (new Date(value as string).getFullYear() >= txtDate.value?.getFullYear() && new Date(value as string).getMonth() >= txtDate.value?.getMonth() && new Date(value as string).getDate() > txtDate.value?.getDate())
                : Number(value) > Number(search);
              }
            );
        }
        else if (columnOperand === 'GreaterThanOrEqual') {
            gridData.value = gridData.value.filter(item =>
              {
                const value = getValueByPath(item, columnField);
                return selectedColumn.value.id === "invoice.issueDate" && txtDate.value ?
                new Date(value as string).getFullYear() > txtDate.value?.getFullYear() ||
                (new Date(value as string).getFullYear() >= txtDate.value?.getFullYear() && new Date(value as string).getMonth() > txtDate.value?.getMonth()) ||
                (new Date(value as string).getFullYear() >= txtDate.value?.getFullYear() && new Date(value as string).getMonth() >= txtDate.value?.getMonth() && new Date(value as string).getDate() >= txtDate.value?.getDate())
                : Number(value) >= Number(search);
              }
            );
        }
        else if (columnOperand === 'LessThan') {
            gridData.value = gridData.value.filter(item =>
              {
                const value = getValueByPath(item, columnField);
                return selectedColumn.value.id === "invoice.issueDate" && txtDate.value ?
                new Date(value as string).getFullYear() < txtDate.value?.getFullYear() ||
                (new Date(value as string).getFullYear() <= txtDate.value?.getFullYear() && new Date(value as string).getMonth() < txtDate.value?.getMonth()) ||
                (new Date(value as string).getFullYear() <= txtDate.value?.getFullYear() && new Date(value as string).getMonth() <= txtDate.value?.getMonth() && new Date(value as string).getDate() < txtDate.value?.getDate())
                : Number(value) < Number(search);
              }
            );
        }
        else if (columnOperand === 'LessThanOrEqual') {
            gridData.value = gridData.value.filter(item =>
              {
                const value = getValueByPath(item, columnField);
                return selectedColumn.value.id === "invoice.issueDate" && txtDate.value ?
                new Date(value as string).getFullYear() < txtDate.value?.getFullYear() ||
                (new Date(value as string).getFullYear() <= txtDate.value?.getFullYear() && new Date(value as string).getMonth() < txtDate.value?.getMonth()) ||
                (new Date(value as string).getFullYear() <= txtDate.value?.getFullYear() && new Date(value as string).getMonth() <= txtDate.value?.getMonth() && new Date(value as string).getDate() <= txtDate.value?.getDate())
                : Number(value) <= Number(search);
              }
            );
        }
        else if (columnOperand === 'BeginsWith') {
            gridData.value = gridData.value.filter(item =>
              {
                const value = getValueByPath(item, columnField);
                return String(value).toLowerCase().startsWith(search.toLowerCase());
              }
            );
        }
        else if (columnOperand === 'Contains') {
            gridData.value = gridData.value.filter(item =>
              {
                const value = getValueByPath(item, columnField);
                return String(value).toLowerCase().includes(search.toLowerCase());
              }
            );
        }
        else if (columnOperand === 'Between' && searchSecondNumber.value !== undefined) {
            gridData.value = gridData.value.filter(item =>
              {
                const value = getValueByPath(item, columnField);
                return selectedColumn.value.id === "invoice.issueDate" && txtDate.value && txtSecondDate.value ?
                (new Date(value as string).getFullYear() > txtDate.value?.getFullYear() ||
                (new Date(value as string).getFullYear() >= txtDate.value?.getFullYear() && new Date(value as string).getMonth() > txtDate.value?.getMonth()) ||
                (new Date(value as string).getFullYear() >= txtDate.value?.getFullYear() && new Date(value as string).getMonth() >= txtDate.value?.getMonth() && new Date(value as string).getDate() >= txtDate.value?.getDate())) &&
                (new Date(value as string).getFullYear() < txtSecondDate.value?.getFullYear() ||
                (new Date(value as string).getFullYear() <= txtSecondDate.value?.getFullYear() && new Date(value as string).getMonth() < txtSecondDate.value?.getMonth()) ||
                (new Date(value as string).getFullYear() <= txtSecondDate.value?.getFullYear() && new Date(value as string).getMonth() <= txtSecondDate.value?.getMonth() && new Date(value as string).getDate() <= txtSecondDate.value?.getDate()))
                : Number(value) >= Number(search) && (typeof searchSecondNumber.value !== 'undefined' && Number(value) <= Number(searchSecondNumber.value));
              }
            );
        }
    }
}

function clearGridFilter() {
    logger.debug('clearGridFilter')
    filterExpanded.value = false
    currentPage.value = 1 // Reset to first page when clearing filter
    getInvoiceList()
}

</script>

<template>
  <div class="open-orders-container">
    <div class="orders-header">
      <div class="title-section">
        <h2 class="pageHeader">{{ t('dashboard.invoice.title') }} <span class="order-count">{{ gridData.length }} {{ t('dashboard.invoice.orders') }}</span></h2>
        <p class="pageSubSubHeader">{{ t('dashboard.invoice.description') }}</p>
      </div>

      <div class="tab-navigation">
        <div class="invoice-list-status-filter-div">
          <button
            :class="['tab-button', activeTab === 'all' ? 'active' : '']"
            @click="setActiveTab('all')"
          >
            {{ t('dashboard.invoice.allInvoices') }}
          </button>
          <button
            :class="['tab-button', activeTab === 'NotPaid' ? 'active' : '']"
            @click="setActiveTab('NotPaid')"
          >
            {{ t('dashboard.invoice.NotPaid') }}
          </button>
          <button
            :class="['tab-button', activeTab === 'Paid' ? 'active' : '']"
            @click="setActiveTab('Paid')"
          >
            {{ t('dashboard.invoice.Paid') }}
          </button>
        </div>
        <div class="invoice-list-filter-icon-div">
          <KButton
            :svg-icon="filterIcon"
            class="invoice-list-filter-icon"
            @click="gridFilterDrawerClick"
          ></KButton>
        </div>
      </div>
    </div>

    <!-- Desktop Grid View -->
    <div id="invoiceListGridRowDiv" class="invoices-grid desktop-grid-view">
      <Grid
        :data-items="gridData"
        :resizable="true"
        :sortable="true"
        :sort="sort"
        :pageable="false"
        :columns="columns"
        @sortchange="sortChangeHandler"
        :loader="loader"
      >
        <template v-slot:orderNumberTemplate="{ props }">
          <td v-if="props.rowType !== 'groupHeader'" class="invoice-customer">
            <KButton
              v-if="isOrderViewable(props.dataItem)"
              class="base-hyper-link"
              :fill-mode="'link'"
              @click="viewOrder($event, props.dataItem.beeNumberCustomer, props.dataItem.eventIdToken)">
              {{ props.dataItem.aoeCartId }}
            </KButton>
            <span v-else class="k-button k-button-md k-rounded-md k-button-link k-button-link-base base-hyper-link invoice-order-disabled">
              <span class="k-button-text">{{ props.dataItem.aoeCartId }}</span>
            </span>
          </td>
        </template>

        <template v-slot:customerNameTemplate="{ props }">
          <td v-if="props.rowType !== 'groupHeader'" class="invoice-customer">
            {{ props.dataItem.customerFirstName }} {{ props.dataItem.customerLastName }}
          </td>
        </template>

        <template v-slot:invoiceNumberTemplate="{ props }">
          <td v-if="props.rowType !== 'groupHeader'" class="invoice-customer">
            <KButton
              class="base-hyper-link"
              :fill-mode="'link'"
              @click="viewInvoice($event, props.dataItem.invoice?.invoiceNumber)">
              {{ props.dataItem.invoice?.invoiceNumber }}
            </KButton>
          </td>
        </template>

        <template v-slot:invoiceIssueDateTemplate="{ props }">
          <td v-if="props.rowType !== 'groupHeader'" class="invoice-issue-date-column">
            {{ formatDateAsDDMMYYYY(props.dataItem.invoice?.issueDate) }}
          </td>
        </template>

        <template v-slot:statusTemplate="{ props }">
          <td v-if="props.rowType !== 'groupHeader'" class="invoice-customer">
            <span :class="['status-badge', props.dataItem.invoice?.invoiceStatus.localizedValue]">{{ props.dataItem.invoice?.invoiceStatus.localizedValue }}</span>
          </td>
        </template>

        <template v-slot:amountTemplate="{ props }">
          <td v-if="props.rowType !== 'groupHeader'">
            {{ FormatCurrency(props.dataItem.total, props.dataItem.currencyId) }}
          </td>
        </template>
      </Grid>
    </div>

    <!-- Mobile Card View -->
    <div class="mobile-card-view">
      <div v-if="loader" class="loading-message">{{ t('labels.Loading') }}</div>
      <div v-else-if="gridData.length === 0" class="no-invoices-message">
        {{ t('dashboard.invoice.noInvoicesFound') }}
      </div>
      <div v-else class="invoices-cards-container">
        <div v-for="invoice in paginatedData" :key="invoice.aoeCartId" class="invoice-card">
          <div class="invoice-card-header">
            <div class="invoice-card-title-row">
              <span class="invoice-card-label">{{ t('dashboard.invoice.orderNumber') }}:</span>
              <KButton
                v-if="isOrderViewable(invoice)"
                class="base-hyper-link invoice-card-link-btn"
                :fill-mode="'link'"
                @click="viewOrder($event, invoice.beeNumberCustomer, invoice.eventIdToken)">
                {{ invoice.aoeCartId }}
              </KButton>
              <span v-else class="k-button k-button-md k-rounded-md k-button-link k-button-link-base base-hyper-link invoice-card-link-btn invoice-order-disabled">
                <span class="k-button-text">{{ invoice.aoeCartId }}</span>
              </span>
            </div>
            <span :class="['status-badge', invoice.invoice?.invoiceStatus.localizedValue]">{{ invoice.invoice?.invoiceStatus.localizedValue }}</span>
          </div>
          <div class="invoice-card-body">
            <div class="invoice-card-row">
              <span class="invoice-card-label">{{ t('dashboard.invoice.eventId') }}:</span>
              <span class="invoice-card-value">{{ invoice.eventId }}</span>
            </div>
            <div class="invoice-card-row">
              <span class="invoice-card-label">{{ t('dashboard.invoice.customerName') }}:</span>
              <span class="invoice-card-value">{{ invoice.customerFirstName }} {{ invoice.customerLastName }}</span>
            </div>
            <div class="invoice-card-row">
              <span class="invoice-card-label">{{ t('dashboard.invoice.invoiceNumber') }}:</span>
              <KButton
                v-if="invoice.invoice?.invoiceNumber"
                class="base-hyper-link invoice-card-link-btn"
                :fill-mode="'link'"
                @click="viewInvoice($event, invoice.invoice.invoiceNumber)">
                {{ invoice.invoice.invoiceNumber }}
              </KButton>
              <span v-else class="invoice-card-value">--</span>
            </div>
            <div class="invoice-card-row">
              <span class="invoice-card-label">{{ t('dashboard.invoice.reads') }}:</span>
              <span class="invoice-card-value">{{ invoice.invoice?.numberOfOpens }}</span>
            </div>
            <div class="invoice-card-row">
              <span class="invoice-card-label">{{ t('dashboard.invoice.amount') }}:</span>
              <span class="invoice-card-value">{{ FormatCurrency(invoice.total, invoice.currencyId) }}</span>
            </div>
            <div class="invoice-card-row">
              <span class="invoice-card-label">{{ t('dashboard.invoice.issueDate') }}:</span>
              <span class="invoice-card-value">{{ formatDateAsDDMMYYYY(invoice.invoice?.issueDate) }}</span>
            </div>
          </div>
        </div>

        <!-- Mobile Pagination Controls -->
        <div v-if="totalPages > 1" class="mobile-pagination">
          <div class="pagination-info">
            {{ t('rma.Showing') }} {{ (currentPage - 1) * itemsPerPage + 1 }}-{{ Math.min(currentPage * itemsPerPage, gridData.length) }} {{ t('rma.Of') }} {{ gridData.length }}
          </div>
          <div class="pagination-controls">
            <button
              :disabled="currentPage === 1"
              @click="goToPage(1)"
              class="pagination-page-btn pagination-nav-btn"
              :title="t('rma.FirstPage')"
            >
              &laquo;
            </button>
            <button
              :disabled="currentPage === 1"
              @click="previousPage"
              class="pagination-page-btn pagination-nav-btn"
              :title="t('rma.PreviousPage')"
            >
              &lt;
            </button>
            <div class="pagination-current">
              {{ currentPage }} {{ t('rma.Of') }} {{ totalPages }}
            </div>
            <button
              :disabled="currentPage === totalPages"
              @click="nextPage"
              class="pagination-page-btn pagination-nav-btn"
              :title="t('rma.NextPage')"
            >
              &gt;
            </button>
            <button
              :disabled="currentPage === totalPages"
              @click="goToPage(totalPages)"
              class="pagination-page-btn pagination-nav-btn"
              :title="t('rma.LastPage')"
            >
              &raquo;
            </button>
          </div>
        </div>
      </div>
    </div>

    <Drawer
        :expanded="filterExpanded"
        :position="'end'"
        :mode="'overlay'"
        :class="[{ 'd-none': !filterExpanded }, 'invoice-filter-drawer']"
        @overlayclick="gridFilterDrawerClick"
        :items="
          filterDrawerItems.map((item, index) => ({
            ...item,
          }))
        "
        :item="'CustomItem'"
      >
        <template v-slot:CustomItem="{ props }">
          <DrawerItem v-bind="props" @click="props.onClick">
            <div class="aoe-cart-detail container">
              <div id="orderListFilterHeader" class="row mt-3">
                <div class="col-12 order-list-filter-header pageHeader">
                  {{ t('dashboard.invoice.GridFilterHeader') }}
                </div>
              </div>
              <div id="orderListFilterSubHeader" class="row mt-3">
                <div class="col-12 order-list-filter-subheader pageSubSubHeader">
                  {{ t('dashboard.invoice.GridFilterSubHeader') }}
                </div>
              </div>
              <div id="invoiceFilterByStatus" class="row mt-3">
                <div class="col-12 order-list-filters">
                    {{ t('dashboard.invoice.FilterByStatus') }}
                </div>
              </div>
              <div id="invoiceFilterByStatusDDL" class="row mt-3">
                <div class="col-12 invoice-list-filters">
                    <DropDownList
                      id="invoiceStatusDDL"
                      :data-items='invoiceStatusDDL'
                      :value= "selectedInvoiceStatus"
                      :text-field="'text'"
                      :data-item-key="'id'"
                      @change="dropDownChangeHandler('invoiceStatusDDL', $event.target.value)">
                    </DropDownList>
                </div>
              </div>
              <div id="invoiceFilterByPeriod" class="row mt-3">
                <div class="col-12 invoice-list-filters">
                    {{ t('dashboard.invoice.FilterByPeriod') }}
                </div>
              </div>
              <div id="invoiceFilterByPeriodDDL" class="row mt-3">
                <div class="col-12 invoice-list-filters">
                    <DropDownList
                      id="periodDDL"
                      :data-items='periodDDL'
                      :value= "selectedPeriod"
                      :text-field="'text'"
                      :data-item-key="'id'"
                      @change="dropDownChangeHandler('periodDDL', $event.target.value)">
                    </DropDownList>
                </div>
              </div>
              <div id="orderListFilterByColumn" class="row mt-3">
                <div class="col-12 order-list-filters">
                    {{ t('orderList.FilterByColumn') }}
                </div>
              </div>
              <div id="orderListFilterByColumnDDL" class="row mt-3">
                <div class="col-12 order-list-filters">
                    <DropDownList
                      id="columnDDL"
                      :data-items='columnDDL'
                      :value= "selectedColumn"
                      :text-field="'text'"
                      :data-item-key="'id'"
                      @change="dropDownChangeHandler('columnDDL', $event.target.value)">
                    </DropDownList>
                </div>
              </div>
              <div id="orderListFilterByColumnOperandDDL" class="row mt-1">
                <div class="col-12 order-list-filters">
                    <DropDownList
                      id="columnOperandDDL"
                      :data-items='columnOperandDDL'
                      :value= "selectedColumnOperand"
                      :text-field="'text'"
                      :data-item-key="'id'"
                      @change="dropDownChangeHandler('columnOperandDDL', $event.target.value)">
                    </DropDownList>
                </div>
              </div>
              <div id="orderListFilterByColumnOperandDDL" class="row mt-1">
                <div v-if="selectedColumn.id != 'invoice.issueDate' && selectedColumn.id != 'eventId' && selectedColumn.id != 'aoeCartId'" class="col-12 order-list-filters">
                    <KTextBox
                      :rounded="'large'"
                      :theme-color="'primary'"
                      v-model="searchText"
                      :placeholder="t('orderList.PlaceHolderSearchFilter')"
                    ></KTextBox>
                </div>
                <div v-if="selectedColumn.id == 'invoice.issueDate'" class="col-12 order-list-filters">
                    <LocalizationProvider :language="locale">
                      <DatePicker
                        :value="txtDate"
                        @change="dateChangeHandler($event)"
                        :placeholder="t('orderList.PlaceHolderSearchFilterDate')"
                        :format="dateFormat"
                        :formatPlaceholder="formatPlaceholder">
                      </DatePicker>
                    </LocalizationProvider>
                </div>
                <div v-if="selectedColumn.id == 'eventId' || selectedColumn.id == 'aoeCartId'" class="col-12 order-list-filters">
                  <KNumericTextBox
                      :rounded="'large'"
                      :theme-color="'primary'"
                      v-model="searchNumber"
                      :placeholder="t('orderList.PlaceHolderSearchFilterNumber')"
                  ></KNumericTextBox>
                </div>
                <div v-if="selectedColumnOperand.id == 'Between'" class="col-12 invoice-list-filters">
                  {{ t('orderList.BetweenAnd') }}
                </div>
                <div v-if="selectedColumnOperand.id == 'Between' && selectedColumn.id == 'invoice.issueDate'" class="col-12 invoice-list-filters">
                    <LocalizationProvider :language="locale">
                      <DatePicker
                        :value="txtSecondDate"
                        @change="secondDateChangeHandler($event)"
                        :placeholder="t('orderList.PlaceHolderSearchFilterSecondDate')"
                        :format="dateFormat"
                        :formatPlaceholder="formatPlaceholder">
                      </DatePicker>
                    </LocalizationProvider>
                </div>
                <div v-if="selectedColumnOperand.id == 'Between' && (selectedColumn.id == 'eventId' || selectedColumn.id == 'aoeCartId')" class="col-12 invoice-list-filters">
                  <KNumericTextBox
                      :rounded="'large'"
                      :theme-color="'primary'"
                      v-model="searchSecondNumber"
                      :placeholder="t('orderList.PlaceHolderSearchFilterSecondNumber')"
                  ></KNumericTextBox>
                </div>
              </div>
              <div id="orderListFilterByColumnOperandDDL" class="row mt-3">
                <div class="col-6 invoice-list-filter-buttons text-center">
                    <KButton
                      :theme-color="'checkoutContinue'"
                      class="baseBtn primaryBtn invoice-list-button invoice-list-apply-filter-btn"
                      @click="applyGridFilter"
                    >{{ t('orderList.ApplyFilterButton') }}</KButton>
                </div>
                <div class="col-6 invoice-list-filter-buttons text-center">
                    <KButton
                      :theme-color="'checkoutContinue'"
                      class="baseBtn secondaryBtn invoice-list-button invoice-list-clear-filter-btn"
                      @click="clearGridFilter"
                    >{{ t('orderList.ClearFilterButton') }}</KButton>
                </div>
              </div>
            </div>
          </DrawerItem>
        </template>
      </Drawer>
  </div>
</template>

<style lang="scss">
.open-orders-container {
  color: #333;
  padding: $card-padding;
  padding-top: 5px;
  background-color: var(--vt-c-white);
  border-radius: $radius-md;
  box-shadow: 0 10px 15px rgba(black, 0.1);
}

.orders-header {
  margin-bottom: 20px;
}

.title-section {
  margin-bottom: 15px;
}

.order-count {
  @include typography-small;
  @include font-weight-regular;
  color: #666;
  background-color: #e9f5f7;
  padding: $spacing-1 $spacing-2;
  border-radius: $radius-lg;
  margin-left: 10px;
}

.tab-navigation {
  display: flex;
  border-bottom: 1px solid #e0e0e0;
  margin-top: 20px;
}

.tab-button {
  padding: $spacing-2-5 $spacing-5;
  background: none;
  border: none;
  @include typography-small;
  cursor: pointer;
  color: #666;
  position: relative;
  transition: color 0.2s;
}

.tab-button.active {
  @include font-weight-medium;
  color: var(--vt-c-teal-dark);
}

.tab-button.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: var(--vt-c-teal-dark);
}

.orders-grid {
  margin-top: 20px;
  overflow-x: auto;
}

.orders-table {
  width: 100%;
  border-collapse: collapse;
}

.orders-table th {
  text-align: left;
  padding: $spacing-3 $spacing-4;
  @include typography-small;
  @include font-weight-medium;
  border-bottom: 1px solid #e0e0e0;
  color: #666;
  cursor: pointer;
}

.orders-table td {
  @include typography-small;
  padding: $spacing-3 $spacing-4;
  border-bottom: 1px solid var(--vt-c-white-soft);
}

.sort-icon {
  display: inline-block;
  margin-left: 5px;
  color: #999999;
}

.status-badge {
  @include font-weight-medium;
  display: inline-block;
  padding: $spacing-1 $spacing-2-5;
  border-radius: $radius-lg;
}

.status-badge.Issued {
  background-color: #5f8397;
}

.status-badge.Paid {
  background-color: #81f38a;
}

.action-button {
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  padding: $spacing-1;
  border-radius: $radius-sm;
}

.action-button:hover {
  background-color: var(--vt-c-white-soft);
}

.selected-row {
  background-color: #fefefe;
}

.grid-loading .k-grid-norecords .k-table-td {
  content-visibility: hidden;
  height: 50px;
}

.invoice-list-status-filter-div {
  flex: 1;
  display: flex;
  gap: $spacing-2-5;
  flex-wrap: wrap;
}

.invoice-list-filter-icon-div {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.k-menu-popup .k-menu-link-text {
    padding: $spacing-1;
}

/* The Kendo theme sets `pointer-events: none` on .k-disabled. Inside a menu popup that makes a
   disabled item inert to the mouse, so moving onto one never fires its mouseover; Kendo only
   sees the mouseleave of the item you came from, decides the pointer left the menu, and closes
   the popup after hoverCloseDelay. That is why the actions vanished on hover.
   Restoring pointer events keeps the popup open and lets the reason line below be read. It
   cannot make the items clickable: Menu.onItemClick and onKeyDown both bail on item.disabled,
   and selectHandler re-checks isInvoiceActionable before doing anything. */
.invoice-action-popup .k-disabled {
  pointer-events: auto;
}

/* The cursor has to be reset on .k-menu-link, not just the .k-item: the theme sets
   `.k-menu-link { cursor: pointer }` on the inner element that actually sits under the
   pointer, and its own `.k-disabled .k-link` reset never matches because the menu uses
   the class k-menu-link. Left as the plain arrow so the rows do not read as actionable. */
.invoice-action-popup .k-disabled,
.invoice-action-popup .k-disabled .k-menu-link {
  cursor: default;
}

/* With pointer events back, hover would otherwise highlight the disabled rows as if live. */
.invoice-action-popup .k-disabled:hover,
.invoice-action-popup .k-disabled.k-hover,
.invoice-action-popup .k-disabled .k-menu-link:hover,
.invoice-action-popup .k-disabled .k-menu-link.k-hover,
.invoice-action-popup .k-disabled .k-menu-link.k-focus {
  background-color: transparent;
  color: inherit;
  box-shadow: none;
}

/* The explanatory line: readable rather than greyed out like the actions it describes. */
.invoice-action-popup .invoice-action-reason {
  opacity: 1;
  white-space: nowrap;
}

.invoice-action-popup .invoice-action-reason .k-menu-link-text {
  @include typography-caption;
  color: #666;
}

/* Mobile Card View Styles */
.mobile-card-view {
  display: none;
}

.mobile-pagination {
  margin-top: $spacing-6;
  padding-top: $spacing-4;
  border-top: 1px solid #e0e0e0;
}

.pagination-info {
  @include typography-small;
  color: #666;
  text-align: center;
  margin-bottom: $spacing-4;
}

.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: $spacing-2;
  flex-wrap: wrap;
}

.pagination-current {
  @include typography-small;
  @include font-weight-medium;
  padding: $spacing-2 $spacing-3;
  min-width: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #333;
}

.pagination-pages {
  display: flex;
  gap: $spacing-2;
  flex-wrap: wrap;
  justify-content: center;
}

.pagination-page-btn {
  @include typography-small;
  @include font-weight-medium;
  padding: $spacing-2 $spacing-3;
  min-width: 36px;
  background: var(--vt-c-white);
  border: 1px solid #e0e0e0;
  border-radius: $radius-sm;
  color: #666;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pagination-page-btn:hover {
  background-color: var(--vt-c-white-soft);
  border-color: var(--vt-c-teal-dark);
}

.pagination-page-btn.active {
  background-color: var(--vt-c-teal-dark);
  color: var(--vt-c-white);
  border-color: var(--vt-c-teal-dark);
}

.pagination-page-btn.ellipsis {
  cursor: default;
  pointer-events: none;
  border-color: transparent;
  background: transparent;
}

.pagination-page-btn.ellipsis:hover {
  background: transparent;
  border-color: transparent;
}

.pagination-nav-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.desktop-grid-view {
  display: block;
}

.invoices-cards-container {
  display: flex;
  flex-direction: column;
  gap: $spacing-4;
  margin-top: 20px;
}

.invoice-card {
  background: var(--vt-c-white);
  border: 1px solid #e0e0e0;
  border-radius: $radius-md;
  overflow: hidden;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.invoice-card:hover {
  box-shadow: 0 4px 12px rgba(black, 0.1);
  transform: translateY(-2px);
}

.invoice-card-header {
  padding: $spacing-4;
  background-color: var(--vt-c-white-soft);
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: $spacing-2-5;
}

.invoice-card-title-row {
  display: flex;
  align-items: center;
  gap: $gap-sm;
  flex-wrap: wrap;
}

.invoice-card-link-btn {
  @include typography-small;
  padding: 0 !important;
  min-height: auto !important;
}

/* Non-event orders that are canceled (or otherwise closed) reuse the Kendo link
   button's classes for exact box/typography alignment with the clickable rows,
   but read as non-clickable: no underline, slightly faded, no hover/pointer.
   pointer-events:none also stops the k-button-link hover from re-adding the
   underline. Two-class selector to override the global .base-hyper-link. */
.base-hyper-link.invoice-order-disabled {
  text-decoration: none;
  opacity: 0.8;
  cursor: default;
  pointer-events: none;
}

.invoice-card-body {
  padding: $spacing-4;
}

.invoice-card-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: $spacing-2 0;
  border-bottom: 1px solid var(--vt-c-white-soft);
  gap: $spacing-2-5;
}

.invoice-card-row:last-child {
  border-bottom: none;
}

.invoice-card-label {
  @include typography-small;
  @include font-weight-medium;
  color: #666;
  flex-shrink: 0;
}

.invoice-card-value {
  @include typography-small;
  @include font-weight-regular;
  color: #333;
  text-align: right;
  word-break: break-word;
}

.loading-message,
.no-invoices-message {
  @include typography-body;
  text-align: center;
  padding: $spacing-10 $spacing-5;
  color: #666;
}

/* !important required: Override Kendo's inline style width with responsive min() — matches the proven cart-summary drawer pattern, keeps a visible/clickable overlay strip at all breakpoints */
.invoice-filter-drawer .k-drawer-wrapper {
  width: min(80vw, 420px) !important;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .open-orders-container {
    padding: $spacing-4;
  }

  .orders-header {
    margin-bottom: 15px;
  }

  .title-section {
    margin-bottom: 15px;
  }

  .title-section .pageHeader {
    @include typography-h4;
  }

  .title-section .pageSubSubHeader {
    @include typography-small;
  }

  .tab-navigation {
    flex-direction: column;
    gap: $spacing-4;
    align-items: stretch;
  }

  .invoice-list-status-filter-div {
    width: 100%;
    justify-content: flex-start;
    border-bottom: 1px solid #e0e0e0;
    padding-bottom: 10px;
  }

  .invoice-list-filter-icon-div {
    width: 100%;
    justify-content: stretch;
  }

  .invoice-list-filter-icon {
    width: 100%;
    justify-content: center;
  }

  .tab-button {
    @include typography-caption;
    padding: $spacing-2 $spacing-4;
  }

  .order-count {
    display: block;
    margin-left: 0;
    margin-top: 8px;
  }

  /* Hide desktop grid, show mobile cards */
  .desktop-grid-view {
    display: none;
  }

  .mobile-card-view {
    display: block;
  }

  /* Stack filter buttons vertically on mobile to prevent overlap */
  .invoice-list-filter-buttons.col-6 {
    width: 100%;
    max-width: 100%;
    flex: 0 0 100%;
    margin-bottom: $spacing-2;
  }

  .invoice-list-filter-buttons.col-6:last-child {
    margin-bottom: 0;
  }
}

</style>
